import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { message, Spin } from 'antd';
import styled from 'styled-components';

import { ArrowLeft2Icon } from '@assets/icons/ArrowLeft2';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useGetJobsBoardPost } from '@hooks/jobsBoard/useGetJobsBoardPost';
import { useCastingContact } from '@hooks/shared/useCastingContact';
import {
  getApplicationDeadlineInfo,
  getApplicationUuidFromUrl,
  handleJobPostDescriptionDisplay,
  JobPostInput,
} from '@lib/utils/applicationHelper';
import { StyledGapContainer } from '@views/auth/signup/styles';
import { CastingContactInfo } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/CastingContactInfo';
import { JobPostApplySection } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/JobPostApplySection';
import { JobPostDescription } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/JobPostDescription';
import { PostDatePosted } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/PostDatePosted';
import { PostHeader } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/PostHeader/PostHeader';
import { PostMainInfo } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/PostMainInfo';
import { PostSecondaryInfo } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/PostSecondaryInfo/PostSecondaryInfo';
import { ShareApplicationButton } from '@views/jobsBoard/JobsBoardSection/JobsBoardPosts/components/ShareButton';
import { StyledPostInfo } from '@views/styles';

export const PublicApplicationView = () => {
  const router = useRouter();
  const applicationUuid = useMemo(
    () => getApplicationUuidFromUrl(router.asPath),
    [router.asPath]
  );
  if (!applicationUuid) {
    return <StyledContainer>Something went wrong</StyledContainer>;
  }

  return <PublicApplicationViewContent applicationUuid={applicationUuid} />;
};

type PublicApplicationViewContentProps = {
  applicationUuid: string;
};
const PublicApplicationViewContent = ({
  applicationUuid,
}: PublicApplicationViewContentProps) => {
  const {
    data: jobPost,
    isLoading,
    isError,
  } = useGetJobsBoardPost(applicationUuid);

  const castingContact = useCastingContact({
    projectLocationCountry: jobPost?.project_location?.country,
    projectCastingContactEmail: jobPost?.project_casting_contact_email,
    projectCastingContactPhone: jobPost?.project_casting_contact_phone,
    projectCompanyName: jobPost?.project_casting_company_name,
  });

  const { hasDeadline } = useMemo(
    () =>
      getApplicationDeadlineInfo(
        jobPost?.jobs_board_close_submissions_date_time,
        jobPost?.application_status,
        jobPost?.project_location?.country
      ),
    [
      jobPost?.jobs_board_close_submissions_date_time,
      jobPost?.application_status,
      jobPost?.project_location?.country,
    ]
  );

  const {
    hasRateNotes,
    hasDescription,
    unionFittingRateNote,
    unionInterviewRateNote,
    nonUnionFittingRateNote,
    nonUnionInterviewRateNote,
  } = useMemo(
    () => handleJobPostDescriptionDisplay(jobPost as JobPostInput),
    [jobPost]
  );

  const onShareButtonClick = useCallback(() => {
    if (!applicationUuid) {
      message.error('Something went wrong');
      return;
    }

    const url = `${window.location.origin}/apps/${applicationUuid}`;
    navigator.clipboard.writeText(url);
    message.success('Link copied');
  }, [applicationUuid]);

  const router = useRouter();
  const onBackButtonClick = useCallback(() => {
    router.push('/job-board');
  }, [router]);

  if (isLoading) {
    return (
      <StyledFullContainer>
        <StyledContainer>
          <StyledLoadingContainer>
            <Spin />
          </StyledLoadingContainer>
        </StyledContainer>
      </StyledFullContainer>
    );
  }

  if (isError || !jobPost) {
    return (
      <StyledFullContainer>
        <StyledContainer>Something went wrong</StyledContainer>
      </StyledFullContainer>
    );
  }

  return (
    <StyledFullContainer>
      <StyledContainer hasDeadline={hasDeadline}>
        <StyledGapContainer gap={23.5}>
          <StyledMainInfo>
            <StyledHeaderWithContent>
              <StyledHeader>
                <UnstyledButton
                  className='back-button'
                  onClick={onBackButtonClick}
                >
                  <div className='icon-container'>
                    <ArrowLeft2Icon />
                  </div>
                  <div>Back to Job Board</div>
                </UnstyledButton>
                <ShareApplicationButton
                  onShareButtonClick={onShareButtonClick}
                />
              </StyledHeader>
              <StyledGapContainer gap={24}>
                <StyledPostHeader
                  postRoleName={jobPost?.role?.name}
                  postProjectName={jobPost?.project_name}
                  postProjectLocation={jobPost?.project_location}
                  postStatus={jobPost?.application_status}
                  postCloseDate={
                    jobPost?.jobs_board_close_submissions_date_time
                  }
                />
                <StyledPostInfo>
                  <PostMainInfo post={jobPost} hasSplitView />
                  <div className='item-info-section-divider' />
                  <PostSecondaryInfo post={jobPost} hasSplitView />
                </StyledPostInfo>
                {(hasRateNotes || hasDescription) && (
                  <JobPostDescription
                    hasDescription={hasDescription}
                    description={jobPost?.description}
                    unionFittingRateNote={unionFittingRateNote}
                    unionInterviewRateNote={unionInterviewRateNote}
                    nonUnionFittingRateNote={nonUnionFittingRateNote}
                    nonUnionInterviewRateNote={nonUnionInterviewRateNote}
                    hasRateNotes={hasRateNotes}
                  />
                )}
                {!!jobPost?.jobs_board_date_time_posted && ( // for this view, the field can be null
                  <PostDatePosted
                    jobs_board_date_time_posted={
                      jobPost?.jobs_board_date_time_posted
                    }
                    projectCountry={jobPost?.project_location?.country}
                  />
                )}
              </StyledGapContainer>
            </StyledHeaderWithContent>
          </StyledMainInfo>
          <CastingContactInfo
            castingContact={castingContact?.castingContact}
            castingPhone={castingContact?.castingPhone}
            castingEmail={castingContact?.castingEmail}
          />
        </StyledGapContainer>
      </StyledContainer>
      <StyledFooter>
        <JobPostApplySection jobPost={jobPost} />
      </StyledFooter>
    </StyledFullContainer>
  );
};

const StyledFullContainer = styled.div`
  color: ${({ theme }) => theme.palette.text[0]};
  font-size: 14px;
  line-height: 20px;
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`;
const StyledFooter = styled.div`
  padding: 24px 20px 48px;
  margin: 0 auto;
  max-width: 720px;

  @media (max-width: 768px) {
    padding: 19px 20px 20px;
    border-top: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  }
`;

type StyledContainerProps = {
  hasDeadline?: boolean;
};
const StyledContainer = styled.div<StyledContainerProps>`
  padding: 48px 20px 0 20px;
  margin: 0 auto;
  max-width: 720px;

  @media (max-width: 768px) {
    padding: 20px;
    height: ${({ hasDeadline }) =>
      hasDeadline
        ? 'calc(100dvh - 65px - 120px)'
        : 'calc(100dvh - 65px - 88px)'};
    overflow-y: auto;
  }
`;

const StyledHeaderWithContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledHeader = styled.div`
  display: flex;

  gap: 8px;
  justify-content: space-between;
  align-items: center;

  .back-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 6px 12px 6px 8px;
    font-weight: 600;

    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 20px;
      padding: 8px 16px 8px 12px;
    }

    .icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;

      @media (max-width: 768px) {
        width: 24px;
        height: 24px;

        svg {
          width: 15.6px;
          height: auto;
        }
      }
    }
  }
`;

const StyledMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  padding-bottom: 23.5px;
`;

const StyledPostHeader = styled(PostHeader)`
  .post-title {
    .post-title-text {
      font-size: 24px;
      line-height: 32px;
      font-weight: 700;

      @media (max-width: 768px) {
        font-size: 20px;
        line-height: 24px;
        font-weight: 600;
      }
    }
  }
`;
